import { z } from 'zod';

import type { ValidationIssue } from '../core/issues.js';
import type { ActivityCheckContext, ActivityDefinition } from './types.js';

import { optionIssues, repeatedKeys, requireOptions, trimmed } from './_checks.js';
import { draftFields, Feedback, Option } from './_shared.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('pick_one');

/**
 * A card's identity. `id` and nothing else — no `value` fallback the way `optionValue` has
 * one — because that is exactly how `correctAnswerId` and the rendered card list resolve a
 * card, and a card sharing its neighbour's `value` costs pick_one nothing.
 */
const cardId = (option: Option): string => trimmed(option.id);

/**
 * `duplicateValueIssues`' rule, on the field `pick_one` actually keys on. Two cards sharing
 * an `id` are indistinguishable to everything downstream: KUI keys the card list on it, and
 * `correctAnswerId` matches both, so either card grades correct and a reveal shows whichever
 * comes first. It fires whether or not an answer is marked — the card list breaks either way.
 */
const duplicateIdIssues = (options: unknown[], ctx: ActivityCheckContext): ValidationIssue[] =>
  repeatedKeys(options, cardId).map(({ index, first, key }) => issue(
    'duplicateId',
    `Card ${index + 1} repeats the id '${key}' that card ${first + 1} already uses, so neither the card list nor correctAnswerId can tell them apart.`,
    [...ctx.path, 'setup', 'options', index, 'id'],
    ctx,
    key,
  ));

/**
 * Pick one, played as image cards. It routes like a dialogue choice, and can also be graded.
 *
 * It keeps its answer checks of its own rather than sharing them with the other graded
 * types: `correctAnswerId` names an option's `id`, where every other type marks its answer
 * with a `value`. Same shape, different field — and a shared helper that took either would
 * stop saying which one a producer got wrong, which is the whole use of the message.
 */
export const pickOne: ActivityDefinition = {
  type: 'pick_one',
  summary: 'A pick one, played as image cards — each card routes, and one may be the correct card.',
  setup: z.looseObject({
    ...draftFields,
    options: z.array(Option).optional(),
    correctAnswerId: z.string().optional(),
    feedback: Feedback.optional(),
    revealAnswer: z.boolean().optional(),
    requireCorrectAnswer: z.boolean().optional(),
  }),
  check: (setup, ctx) => {
    const options = asList(setup.options);
    const issues = [
      ...requireOptions('pick_one', options, ctx),
      ...optionIssues('pick_one', options, ctx, { requireOutcomes: true }),
      ...duplicateIdIssues(options, ctx),
    ];

    const correct = trimmed(setup.correctAnswerId);
    const ids = options.map(option => cardId(asRecord(option) as Option)).filter(Boolean);
    if (correct && !ids.includes(correct)) {
      issues.push(issue(
        'unknownCorrectAnswer',
        `correctAnswerId '${correct}' names no card (ids: ${ids.join(', ') || 'none'}).`,
        [...ctx.path, 'setup', 'correctAnswerId'],
        ctx,
        correct,
      ));
    }

    return issues;
  },
};
